// Página del módulo Mandato con Representación - URI /mandato/:code (independiente del flujo de contratación)
// En esta ruta NUNCA hay llamadas a pago: solo se cargan datos, se genera el PDF del mandato y se sube; el email lo envía el backend.

import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHiringData } from '@/hooks/useHiringData';
import { useLanguage } from '@/hooks/useLanguage';
import { MandateSignature } from '@/components/mandate/MandateSignature';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

export function MandatePage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { details, loading, error } = useHiringData(code || '', { skipAnnexes: true });
  const { t } = useTranslation('mandate');
  const { setLanguage } = useLanguage();

  useEffect(() => {
    if (details?.language) {
      setLanguage(details.language);
    }
  }, [details?.language, setLanguage]);

  useEffect(() => {
    if (!details) return;
    const expirationDate = new Date(details.expires_at);
    if (new Date() > expirationDate) {
      navigate('/expirado');
    }
  }, [details, navigate]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <ArrowPathIcon className="mx-auto mb-4 animate-spin text-primary" width={48} height={48} />
          <p className="text-gray-600">{t('common:buttons.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-red-800 mb-4">Error</h2>
          <p className="text-red-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-primary hover:bg-primary-700 text-white px-6 py-2 rounded-lg font-semibold"
          >
            {t('common:buttons.back')}
          </button>
        </div>
      </div>
    );
  }

  if (!details || !code) {
    return null;
  }

  const handleBack = () => {
    navigate(`/contratacion/${code}?step=3`);
  };

  /** Espera a que el backend reporte mandate_status 'signed' antes de navegar, para evitar loop de redirección en HiringFlow. */
  const waitForMandateSigned = async (): Promise<void> => {
    const { mandateService } = await import('@/services/mandateService');
    const maxAttempts = 5;
    const delayMs = 600;
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const res = await mandateService.getMandateStatus(code);
        if (res.status === 'signed') return;
      } catch {
        // seguir intentando
      }
      if (i < maxAttempts - 1) {
        await new Promise((r) => setTimeout(r, delayMs));
      }
    }
  };

  /** Tras firmar el mandato: el PDF con firma ya se subió y se guardó en el hiring code.
   *  Redirigir al paso 4 (pago) del flujo de contratación para continuar con Stripe. */
  const handleComplete = () => {
    (async () => {
      await waitForMandateSigned();
      sessionStorage.setItem(`mandate_just_signed_${code}`, String(Date.now()));
      navigate(`/contratacion/${code}?step=4`);
    })();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b border-gray-200 py-3 px-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <h1 className="text-lg font-semibold text-gray-800">{t('title')}</h1>
          <a
            href={`/contratacion/${code}?step=3`}
            className="text-sm text-primary hover:underline"
          >
            {t('common:buttons.back')}
          </a>
        </div>
      </div>
      <div className="py-8">
        <MandateSignature
          hiringCode={code}
          details={details}
          isAlreadySigned={details.mandate_status === 'signed'}
          onComplete={handleComplete}
          onBack={handleBack}
        />
      </div>
    </div>
  );
}
